/**
 * ReportRisk.js v2 — Risk Assessment with full breakdown
 * Risk score model v2, breach cost itemized, attack source attribution
 */
import React, { useMemo } from "react";
import { RadialBarChart, RadialBar, ResponsiveContainer,
         PieChart, Pie, Cell, Tooltip } from "recharts";
import { INDUSTRY_COSTS, getAttackSource, getRiskScoreBreakdown } from "../reportUtils";

export default function ReportRisk({
  stats, mlResult, riskScore, riskLabel, alertFatigue, mttd,
  theme, SEVERITY_COLORS: SC, industry = "global"
}) {
  if (!stats) return null;

  const scoreBreakdown = useMemo(() => getRiskScoreBreakdown(stats, mlResult), [stats, mlResult]);
  const attackSource   = useMemo(() => getAttackSource(stats.attackDist || []), [stats]);

  const sevData = [
    { name: "Critical", value: stats.critical, color: SC.critical },
    { name: "High",     value: stats.high,     color: SC.high     },
    { name: "Medium",   value: stats.medium,   color: SC.medium   },
    { name: "Low",      value: stats.low,       color: SC.low      },
    { name: "Info",     value: stats.info,      color: SC.info     },
  ].filter(d => d.value > 0);


  return (
    <div className="report-section page-break" style={{ marginBottom: 24 }}>
      <SectionTitle color="#C62828">Risk Assessment</SectionTitle>

      {/* Row 1: Gauge + Pie + Key Metrics */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>

        {/* Risk Score Gauge + breakdown */}
        <div className="report-card" style={cardStyle(theme)}>
          <SubTitle theme={theme}>OVERALL RISK SCORE</SubTitle>
          <div style={{ height: 150, position: "relative" }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadialBarChart cx="50%" cy="80%" innerRadius="60%" outerRadius="90%"
                startAngle={180} endAngle={0}
                data={[{ value: 100, fill: theme.colors.cardBorder }, { value: riskScore, fill: riskLabel.color }]}>
                <RadialBar dataKey="value" cornerRadius={4} />
              </RadialBarChart>
            </ResponsiveContainer>
            <div style={{ position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", textAlign: "center" }}>
              <div style={{ fontSize: 38, fontWeight: 900, color: riskLabel.color, fontFamily: "monospace" }}>{riskScore}</div>
              <div style={{ fontSize: 11, color: riskLabel.color, fontWeight: 700, letterSpacing: 1 }}>{riskLabel.label}</div>
            </div>
          </div>
          {scoreBreakdown && (
            <div style={{ marginTop: 10, borderTop: `1px solid ${theme.colors.cardBorder}`, paddingTop: 8 }}>
              {[
                { label: "Severity Presence",  val: scoreBreakdown.base, color: SC.critical, note: "Base from highest attack class" },
                { label: "Volume Amplifier",   val: scoreBreakdown.vol,  color: SC.high,     note: "Frequency of confirmed events"  },
                { label: "Attack Diversity",   val: scoreBreakdown.div,  color: SC.medium,   note: "Distinct malicious attack types" },
                { label: "ML Confirmation",    val: Math.round(scoreBreakdown.ml), color: SC.info, note: "ML anomaly rate bonus" },
              ].map(({ label, val, color, note }) => (
                <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0", borderBottom: `1px solid ${theme.colors.cardBorder}` }}>
                  <div>
                    <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1 }}>{label}</div>
                    <div style={{ fontFamily: "monospace", fontSize: 11, color: "#90A4AE" }}>{note}</div>
                  </div>
                  <div style={{ fontFamily: "monospace", fontSize: 14, fontWeight: 900, color }}>{val > 0 ? `+${val}` : "0"}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Severity Pie */}
        <div className="report-card" style={cardStyle(theme)}>
          <SubTitle theme={theme}>SEVERITY DISTRIBUTION</SubTitle>
          <ResponsiveContainer width="100%" height={150}>
            <PieChart>
              <Pie data={sevData} dataKey="value" cx="50%" cy="50%" outerRadius={60}>
                {sevData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip formatter={(v) => [v.toLocaleString()]} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ marginTop: 8 }}>
            {sevData.map(d => (
              <div key={d.name} style={{ display: "flex", justifyContent: "space-between", fontFamily: "monospace", fontSize: 11, padding: "3px 0", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                <span style={{ color: d.color, fontWeight: 700 }}>● {d.name}</span>
                <span style={{ color: theme.colors.textPrimary, fontWeight: 700 }}>{d.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Key Indicators */}
        <div className="report-card" style={cardStyle(theme)}>
          <SubTitle theme={theme}>KEY RISK INDICATORS</SubTitle>
          {[
            { label: "Alert Fatigue Index",  value: `${alertFatigue}%`, color: alertFatigue > 70 ? SC.high : SC.medium, note: "% non-actionable alerts" },
            { label: "MTTD Estimate",        value: mttd,              color: "#00695C", note: "vs 194-day avg (IBM 2024)" },
            { label: "Reporting Source",     value: attackSource.primary, color: theme.colors.accent, note: `${attackSource.type} — ${attackSource.secondary}` },
            { label: "Industry Benchmark",   value: (INDUSTRY_COSTS[industry]||INDUSTRY_COSTS.global).label, color: "#2E7D32",
              note: `$${((INDUSTRY_COSTS[industry]||INDUSTRY_COSTS.global).cost/1000000).toFixed(2)}M IBM 2024 avg` },
            { label: "Breach Cost Analysis", value: "See below →", color: SC.critical, note: "Full breakdown in cost section" },
          ].map(({ label, value, color, note }) => (
            <div key={label} style={{ marginBottom: 10, paddingBottom: 10, borderBottom: `1px solid ${theme.colors.cardBorder}` }}>
              <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted, letterSpacing: 1 }}>{label}</div>
              <div style={{ fontFamily: "monospace", fontSize: 14, fontWeight: 900, color }}>{value}</div>
              <div style={{ fontFamily: "monospace", fontSize: 11, color: theme.colors.textMuted }}>{note}</div>
            </div>
          ))}
        </div>
      </div>


      {/* Row 3: Key Findings Table */}
      <div className="report-card" style={cardStyle(theme)}>
        <SubTitle theme={theme}>KEY FINDINGS RISK TABLE</SubTitle>
        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "monospace", fontSize: 11 }}>
          <thead>
            <tr style={{ background: theme.colors.tableHeader }}>
              {["#","Finding","Value","Risk Level","Source"].map(h => (
                <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: theme.colors.textPrimary, borderBottom: `2px solid ${theme.colors.cardBorder}`, fontSize: 11 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[
              { f:"Total events analyzed",    v:stats.total.toLocaleString(),   r:"INFO",     rc:SC.info,     s:"Dataset"            },
              { f:"Critical severity events", v:stats.critical.toLocaleString(),r:stats.critical>0?"CRITICAL":"OK", rc:stats.critical>0?SC.critical:"#388E3C", s:"ML Analysis" },
              { f:"High severity events",     v:stats.high.toLocaleString(),    r:stats.high>50?"HIGH":"MEDIUM", rc:stats.high>50?SC.high:SC.medium, s:"ML Analysis" },
              { f:"Unique attack types",      v:(stats.attackDist||[]).filter(a=>!['benign','normal'].some(x=>a.name.toLowerCase().includes(x))).length, r:"MEDIUM", rc:SC.medium, s:"Pattern Analysis" },
              { f:"Most common attack",       v:stats.attackDist[0]?.name||"N/A", r:"INFO",  rc:SC.info,     s:"Attack Analysis"    },
              { f:"Top attacker IP",          v:stats.srcIPDist[0]?.name||"N/A",  r:stats.srcIPDist[0]?"HIGH":"INFO", rc:stats.srcIPDist[0]?SC.high:SC.info, s:"Network Analysis" },
              { f:"Primary attack vector port",v:(stats.attackPortDist?.[0]?.name||stats.portDist[0]?.name||"N/A"), r:"HIGH", rc:SC.high, s:"Network Analysis" },
              { f:"Alert fatigue index",      v:`${alertFatigue}%`,             r:alertFatigue>70?"HIGH":"MEDIUM", rc:alertFatigue>70?SC.high:SC.medium, s:"Calculated" },
              { f:"Reporting source",         v:attackSource.primary,           r:"INFO",     rc:SC.info,     s:attackSource.type    },
              { f:"Estimated breach cost",    v:"See Breach Cost Analysis section", r:"CRITICAL", rc:SC.critical, s:"IBM 2024 + Multi-source" },
            ].map((row, i) => (
              <tr key={i} style={{ background:i%2===0 ? theme.colors.rowAlt : theme.colors.card, borderBottom:`1px solid ${theme.colors.cardBorder}` }}>
                <td style={{ padding:"7px 10px", color:theme.colors.textMuted }}>{i+1}</td>
                <td style={{ padding:"7px 10px", color:theme.colors.textPrimary }}>{row.f}</td>
                <td style={{ padding:"7px 10px", color:theme.colors.textPrimary, fontWeight:700 }}>{String(row.v)}</td>
                <td style={{ padding:"7px 10px" }}>
                  <span style={{ padding:"2px 8px", background:`${row.rc}18`, border:`1px solid ${row.rc}44`, borderRadius:4, color:row.rc, fontSize: 11, fontWeight:700 }}>{row.r}</span>
                </td>
                <td style={{ padding:"7px 10px", color:theme.colors.textMuted, fontSize: 11 }}>{row.s}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const cardStyle = (theme) => ({
  background: theme.colors.card,
  border: `1px solid ${theme.colors.cardBorder}`,
  borderRadius: 10, padding: 20,
});
const SectionTitle = ({ children, color="#C62828" }) => (
  <div style={{ fontFamily:"monospace", fontSize:13, fontWeight:900, color, letterSpacing:2, textTransform:"uppercase", borderLeft:`4px solid ${color}`, paddingLeft:12, marginBottom:16 }}>{children}</div>
);
const SubTitle = ({ children, theme }) => (
  <div style={{ fontFamily:"monospace", fontSize:11, color:theme.colors.textMuted, letterSpacing:1, marginBottom:10, fontWeight:700 }}>{children}</div>
);
